import React, { useEffect, useContext, useState, useMemo, useRef, useCallback } from 'react'
import { StatusText } from './style'

import { Eye, Pencil, Trash2, Copy } from 'lucide-react'
import { FreightContext, Freight } from '../../../../contexts/FreightContext'
import axios from '../../../../config/axios'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { RouterOutletType } from '../index2'
import SectionContext from '../../../../contexts/SectionContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import { OverflowCell } from '../../../../components/OverflowCell'
import styled from 'styled-components'
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../components/StandardPage'

const freightScreenKey = 'router_freight'

const TableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;

  .duplicated-row {
    background-color: #fffbe3;
  }
`

const dataFormatter = (isoDateString: string): string => {
  if (!isoDateString) {
    return ''
  }

  const dateUtc = new Date(isoDateString)

  if (isNaN(dateUtc.getTime())) {
    console.error('Data ISO inválida recebida:', isoDateString)
    return ''
  }

  const year = dateUtc.getUTCFullYear()
  const month = dateUtc.getUTCMonth() + 1
  const day = dateUtc.getUTCDate()

  const formattedMonth = month < 10 ? `0${month}` : month
  const formattedDay = day < 10 ? `0${day}` : day

  return `${formattedDay}/${formattedMonth}/${year}`
}

const dateHourFormatter = (isoDateString: string) => {
  const date = new Date(isoDateString)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  const brasiliaOffset = -3
  const utcHours = date.getUTCHours()
  const brasiliaHours = utcHours + brasiliaOffset

  const finalHours = brasiliaHours < 0 ? 24 + brasiliaHours : brasiliaHours
  const finalMinutes = String(date.getUTCMinutes()).padStart(2, '0')

  return `dia ${day}/${month}/${year} às ${String(finalHours).padStart(
    2,
    '0',
  )}:${finalMinutes}`
}

const mapStatus = (status: string): string => {
  if (status === 'A programar') return ''
  if (status === 'Rota programada') return 'pending'
  if (status === 'Endereço pendente' || status === 'Cancelado') return 'cancel'
  if (status === 'Não Entregue') return 'notDelivered'
  if (status === 'Entregue' || status === 'Coletado') return 'completed'
  return status
}

const formatReadyToShip = (freight: Freight): string => {
  if (!freight.readyToShip) return ''
  return new Date(freight.readyToShip).setUTCHours(0, 0, 0, 0) <=
    new Date().setUTCHours(0, 0, 0, 0)
    ? 'Liberado'
    : dataFormatter(freight.readyToShip)
}

const formatObservation = (freight: Freight): string =>
  freight.observation ? freight.observation.replace(/\[CÓPIA\]/gi, '').trim() : ''

const buildFreightColumns = (): StandardColumn<Freight>[] => [
  {
    id: 'companyName',
    label: 'Cliente',
    accessor: (freight) => freight.Company?.companyName || '-',
    cell: (freight) => (
      <OverflowCell text={freight.Company?.companyName || '-'} maxWidth={160} />
    ),
  },
  { id: 'type', label: 'Tipo', accessor: (freight) => freight.type },
  {
    id: 'status',
    label: 'Status',
    accessor: (freight) => freight.status,
    cell: (freight) => (
      <StatusText className={mapStatus(freight.status)}>
        {freight.status}
      </StatusText>
    ),
  },
  {
    id: 'readyToShip',
    label: 'Liberado pra expedir',
    accessor: formatReadyToShip,
  },
  {
    id: 'deadLine',
    label: 'Data limite de entrega',
    accessor: (freight) => (freight.deadLine ? dataFormatter(freight.deadLine) : ''),
  },
  {
    id: 'observation',
    label: 'Observações',
    accessor: formatObservation,
    cell: (freight) => (
      <OverflowCell text={formatObservation(freight)} maxWidth={150} />
    ),
  },
]

interface FreightTableType {
  onChange?: () => void
}

const FreightTable: React.FC<FreightTableType> = () => {
  const { setFreight } = useContext(FreightContext)
  const { setRouterSection } = useContext(SectionContext)
  const [freights, setFreights] = useState<Freight[]>([])
  const [totalFreights, setTotalFreights] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const { setFeedback } = useContext(FeedbackContext)
  const navigate = useNavigate()
  const { search, appliedFilters, setTabIndex, refresh, setLoadingHeader } = useOutletContext<RouterOutletType>()
  const { freightStatusFilter } = useContext(TableFiltersContext)
  const [duplicatedIds, setDuplicatedIds] = useState<number[]>([])
  const isFirstRender = useRef(true)

  const columns = useMemo(() => buildFreightColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(freightScreenKey, columns)

  useEffect(() => {
    const idsRaw = sessionStorage.getItem('duplicatedFreights')
    const ids = idsRaw ? JSON.parse(idsRaw) : []

    if (ids.length > 0) {
      setDuplicatedIds(ids)
    }
    sessionStorage.removeItem('duplicatedFreights')
  }, [freightStatusFilter, search])

  const handleEditFreight = async (id: number) => {
    setFreight(await getFreight(id))
    navigate('create')
  }

  const handleViewFreight = async (id: number) => {
    setFreight(await getFreight(id))
    navigate(`${id}`)
  }

  const getFreight = async (id: number): Promise<Freight> => {
    const response = await axios.get(
      `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight/${id}`,
    )
    return response.data as Freight
  }

  const getFreights = async (
    pageNumber: number,
    statusFilter: string | null = null,
    search: string | null = null,
  ) => {
    try {
      const token = localStorage.getItem('token')
      let url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight?page=${pageNumber}&size=50`
      if (statusFilter && statusFilter !== 'Todos') {
        url += `&status=${encodeURIComponent(statusFilter)}`
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }
      if (appliedFilters && appliedFilters.length > 0) {
        url += `&filters=${encodeURIComponent(JSON.stringify(appliedFilters))}`
      }
      const response = await axios.get(url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      )
      try {
        let countUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/freights/count?1=1`
        if (statusFilter && statusFilter !== 'Todos') {
          countUrl += `&status=${encodeURIComponent(statusFilter)}`
        }
        if (search) {
          countUrl += `&search=${encodeURIComponent(search)}`
        }
        if (appliedFilters && appliedFilters.length > 0) {
          countUrl += `&filters=${encodeURIComponent(JSON.stringify(appliedFilters))}`
        }
        const countResp = await axios.get(countUrl, { headers: { Authorization: `Bearer ${token}` } })
        setTotalFreights(countResp.data?.total ?? null)
      } catch (err) {
        setTotalFreights(null)
      }
      return response.data as Freight[]
    } catch (error) {
      console.error('Erro ao buscar fretes:', error)
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar fretes!',
        statusOk: false,
      })
      return []
    }
  }

  const deleteFreight = async (id: number) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight/${id}`,
      )
      setFreights((prevFreights) =>
        prevFreights.filter((freight) => freight.id !== id),
      )
      setFeedback({
        isOpen: true,
        message: 'Frete deletado!',
        statusOk: true,
      })
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao deletar frete',
        statusOk: false,
      })
    }
  }

  const duplicateFreight = async (id: number) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight/${id}/duplicate`,
      )
      setFeedback({
        isOpen: true,
        message: 'Frete duplicado!',
        statusOk: true,
      })
      const newFreightId = response.data.id
      setDuplicatedIds((prevIds) =>
        prevIds.includes(newFreightId) ? prevIds : [...prevIds, newFreightId],
      )
      fetchInitialFreights()
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao duplicar frete',
        statusOk: false,
      })
    }
  }

  const canDuplicateFreight = (freight: Freight) => {
    const isCompleted =
      freight.status === 'Coletado' || freight.status === 'Entregue'
    const isAlreadyDuplicate = freight.observation
      ?.toUpperCase()
      .includes('[CÓPIA]')
    return !isCompleted && !isAlreadyDuplicate
  }

  const loadMoreFreights = async () => {
    if (!hasMore) {
      return
    }

    try {
      const nextPage = page + 1
      const newFreights = await getFreights(
        nextPage,
        freightStatusFilter,
        search,
      )

      if (newFreights.length === 0 || newFreights.length < 50) {
        setHasMore(false)
      }
      setFreights((prevFreights) => [...prevFreights, ...newFreights])
      setPage(nextPage)
    } catch (error) {
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar mais fretes',
        statusOk: false,
      })
    }
  }

  const fetchInitialFreights = useCallback(async () => {
    setLoadingHeader?.(true)
    try {
      setPage(0)
      setHasMore(true)
      const initialFreights = await getFreights(
        0,
        freightStatusFilter,
        search,
      )
      setFreights(initialFreights)
      if (initialFreights.length < 50) {
        setHasMore(false)
      }
    } catch (error) {
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar fretes iniciais',
        statusOk: false,
      })
      setFreights([])
    } finally {
      setLoadingHeader?.(false)
    }
  }, [freightStatusFilter, search, appliedFilters, setFeedback, setLoadingHeader])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (refresh) fetchInitialFreights()
  }, [refresh])

  useEffect(() => {
    fetchInitialFreights()
  }, [fetchInitialFreights])

  useEffect(() => {
    setTabIndex(0)
    setRouterSection('freight')
  }, [])

  return (
    <TableRoot>
      <StandardDataTable
        columns={columns}
        data={freights}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalFreights}
        onLoadMore={loadMoreFreights}
        hasMore={hasMore}
        countLabel='fretes'
        exportFileName='fretes'
        emptyMessage='Nenhum frete encontrado.'
        rowClassName={(freight) =>
          duplicatedIds.includes(freight.id!) ? 'duplicated-row' : ''
        }
        rowTitle={(freight) =>
          `Criado${freight.createdBy ? ' por: ' + freight.createdBy.name : ''} ${
            freight.createdAt ? dateHourFormatter(freight.createdAt) : ''
          }`
        }
        rowActions={(freight) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleViewFreight(freight.id!) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEditFreight(freight.id!) },
          ...(canDuplicateFreight(freight)
            ? [{ label: 'Duplicar frete', icon: <Copy size={16} />, onClick: () => duplicateFreight(freight.id!) }]
            : []),
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => deleteFreight(freight.id!) },
        ]}
      />
    </TableRoot>
  )
}

export default FreightTable
